import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Boxes, CreditCard, FileText, Menu, Plus, Search, UserPlus } from "lucide-react";

import { cn } from "src/lib/utils";
import { useAuth } from "src/providers/AuthProvider";
import { Button } from "src/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "src/components/ui/popover";

interface PageHeaderProps {
	onOpenMobileNav: () => void;
	/** Opens the global search palette; owned by ProtectedRoute so ⌘K can reach it. */
	onOpenSearch: () => void;
}

const PageHeader = ({ onOpenMobileNav, onOpenSearch }: PageHeaderProps) => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const [hasNotifications] = useState(false);

	// Prototype affordance: ⌘K / Ctrl+K opens global search.
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
				event.preventDefault();
				onOpenSearch();
			}
		};

		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [onOpenSearch]);

	return (
		<header className="tw-sticky tw-top-0 tw-z-20 tw-flex tw-h-[68px] tw-shrink-0 tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-border tw-bg-white tw-px-4 sm:tw-px-6">
			<button
				type="button"
				onClick={onOpenMobileNav}
				aria-label="Open navigation"
				className="tw-rounded-lg tw-p-2 tw-text-text hover:tw-bg-bg lg:tw-hidden"
			>
				<Menu className="tw-h-5 tw-w-5" />
			</button>

			{/* Global search — a button rather than an input, since it opens the ⌘K palette */}
			<button
				type="button"
				onClick={onOpenSearch}
				className="tw-relative tw-hidden tw-max-w-[420px] tw-flex-1 tw-items-center tw-rounded-[10px] tw-border tw-border-border tw-bg-bg tw-py-[9px] tw-pl-9 tw-pr-3 tw-text-left tw-text-[13.5px] tw-text-text-soft tw-transition-colors hover:tw-border-teal hover:tw-bg-white sm:tw-flex"
			>
				<Search className="tw-absolute tw-left-3 tw-h-4 tw-w-4 tw-text-text-soft" />
				<span className="tw-truncate">Search customers, invoices, products...</span>
				<kbd className="tw-ml-auto tw-shrink-0 tw-rounded-[5px] tw-border tw-border-border tw-bg-white tw-px-1.5 tw-py-0.5 tw-text-[10.5px] tw-text-text-soft">
					⌘K
				</kbd>
			</button>

			<div className="tw-ml-auto tw-flex tw-items-center tw-gap-2 sm:tw-gap-3">
				<button
					type="button"
					onClick={onOpenSearch}
					aria-label="Search"
					className="tw-rounded-lg tw-p-2 tw-text-text-soft hover:tw-bg-bg sm:tw-hidden"
				>
					<Search className="tw-h-5 tw-w-5" />
				</button>

				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
							<Plus className="tw-h-4 tw-w-4" />
							<span className="tw-hidden sm:tw-inline">New</span>
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="tw-w-[220px]">
						<DropdownMenuItem onSelect={() => navigate("/invoices/add")}>
							<FileText />
							New Invoice
						</DropdownMenuItem>
						<DropdownMenuItem onSelect={() => navigate("/customers?new=1")}>
							<UserPlus />
							New Customer
						</DropdownMenuItem>
						<DropdownMenuItem onSelect={() => navigate("/inventory?new=1")}>
							<Boxes />
							New Product
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem onSelect={() => navigate("/invoices")}>
							<CreditCard />
							Record Payment
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>

				<Popover>
					<PopoverTrigger asChild>
						<button
							type="button"
							aria-label="Notifications"
							className="tw-relative tw-rounded-lg tw-p-2 tw-text-text-soft tw-transition-colors hover:tw-bg-bg"
						>
							<Bell className="tw-h-5 tw-w-5" />
							{hasNotifications && (
								<span className="tw-absolute tw-right-1.5 tw-top-1.5 tw-h-2 tw-w-2 tw-rounded-full tw-bg-red tw-ring-2 tw-ring-white" />
							)}
						</button>
					</PopoverTrigger>
					<PopoverContent align="end" className="tw-w-[320px] tw-p-0">
						<div className="tw-border-b tw-border-border tw-px-3 tw-py-2.5 tw-text-[13px] tw-font-semibold">Notifications</div>
						<div className="tw-px-4 tw-py-5 tw-text-center tw-text-[13px] tw-text-text-soft">No notifications yet.</div>
						<div className="tw-border-t tw-border-border tw-p-2">
							<Link
								to="/notifications"
								className="tw-block tw-rounded-lg tw-px-2 tw-py-1.5 tw-text-center tw-text-[13px] tw-font-medium tw-text-teal hover:tw-bg-bg"
							>
								View all
							</Link>
						</div>
					</PopoverContent>
				</Popover>

				<Link
					to="/profile"
					className={cn(
						"tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-bg-navy-tint tw-text-[12px] tw-font-bold tw-text-navy lg:tw-hidden"
					)}
					aria-label="Profile"
				>
					{`${user?.first_name?.[0] ?? ""}${user?.last_name?.[0] ?? ""}`.toUpperCase() || "PL"}
				</Link>
			</div>
		</header>
	);
};

export default PageHeader;
